import uuid from 'node-uuid';
import moment from 'moment';
import { get, head, last } from 'lodash';
import { DAILY_LIMIT_ERROR_CODE } from '../../config/';
import { paymentFrequencyOptions } from '../../config/paymentFrequencyOptions';
import { fixAmount, stripCurrencysIllegalCharacters } from '../../helpers/fieldHelpers';
import {
	INIT_PAYMENTS,
	UPDATE_FROM_ACCOUNT,
	UPDATE_TO_ACCOUNT,
	UPDATE_DATE,
	ADD_PAYMENT,
	ADD_MULTIPLE_PAYMENTS,
	UPDATE_TYPE,
	REMOVE_PAYMENT,
	UPDATE_AMOUNT,
	UPDATE_REFERENCE,
	UPDATE_FREQUENCY,
	UPDATE_STOP_PAYMENT_OPTION,
	UPDATE_STOP_PAYMENT_ON_NUMBER,
	UPDATE_STOP_PAYMENT_ON_DATE,
	COPY_DATE_TO_ALL,
	COPY_REF_TO_ALL,
	UPDATE_ALL_DATES,
	UPDATE_ALL_FROM_ACCOUNTS,
	RESET_PAYMENT_AUTHORISATION,
	RESET_DAILY_LIMIT_AUTHORISATION,
	SET_AUTHORISERS,
	CLEAR_PAYMENTS,
} from './actions';

import { RESET_APP } from '../page/actions';
import { CUT_OFF_MISSED_UPDATE_PAYMENT_DATE } from '../calendar/actions';
import { BENEFICIARY_CREATE_SUCCESS } from '../payees/actions';
import {
	SUBMIT_PAYMENT_REVIEW_SUCCESS,
	SUBMIT_PAYMENT_DAILY_LIMIT_REVIEW_SUCCESS,
	SUBMIT_CONFIRMED_PAYMENT_SUCCESS,
	SUBMIT_CONFIRMED_BATCH_PAYMENTS_SUCCESS,
	SUBMIT_REPEATING_PAYMENT_REVIEW_SUCCESS,
	SUBMIT_REPEATING_PAYMENT_REVIEW_FAILURE,
	SUBMIT_CONFIRMED_REPEATING_PAYMENT_SUCCESS,
	SUBMIT_CONFIRMED_REPEATING_PAYMENT_FAILURE,
	UPDATE_PAYMENT_ID,
} from '../api/fasterPaymentActions';
import { isPaymentTRAObligate } from '../../utils/authoriserUtils';
import { INIT_PAYMENTS_FOR_TEMPLATE, INIT_PAYMENTS_FROM_TEMPLATE } from '../templates/actions';
import { isConflictAndDailyLimitError } from '../../helpers/apiResponse';
import { getAuthorisationInfo } from '../../helpers/reducerHelper';

const initialPayments = [];

const defaultPaymentState = ({ reference, ...paymentExcludingReference } = {}) => ({
	toAccount: '',
	fromAccount: '',
	date: undefined,
	type: 'single',
	amount: '',
	reference,
	initialReference: reference,
	frequency: '',
	stopPaymentOption: 'on-cancel',
	stopPaymentOnNumber: undefined,
	stopPaymentOnDate: undefined,
	approverId: undefined,
	user_id: undefined,
	...paymentExcludingReference,
});

const getInitialPaymentState = (state, initialPayment) => {
	const lastPayment = last(state);
	if (lastPayment) {
		return {
			...initialPayment,
			fromAccount: lastPayment.fromAccount,
			date: lastPayment.date,
		};
	}
	return initialPayment;
};

const paymentHandlers = {
	[UPDATE_TO_ACCOUNT]: (state, action) => ({ ...state, toAccount: action.accountId }),

	[UPDATE_DATE]: (state, action) => {
		const { type, stopPaymentOption, stopPaymentOnDate } = state;

		if (stopPaymentOption === 'on-date' && type === 'repeating' && action.date.isAfter(stopPaymentOnDate)) {
			return {
				...state,
				date: action.date,
				stopPaymentOnDate: moment(action.date),
			};
		}
		return {
			...state,
			date: action.date,
		};
	},
	[UPDATE_REFERENCE]: (state, action) => ({ ...state, reference: action.reference }),
	[UPDATE_TYPE]: (state, action) => {
		const { paymentType } = action;
		const offset = paymentType === 'repeating' ? 1 : 0;

		return {
			...state,
			type: paymentType,
			stopPaymentOption: 'on-cancel',
			frequency: '',
			stopPaymentOnNumber: undefined,
			stopPaymentOnDate: undefined,
			date: moment().add(offset, 'day'),
		};
	},
	[UPDATE_AMOUNT]: (state, action) => ({ ...state, amount: action.amount }),
	[UPDATE_FREQUENCY]: (state, action) => ({ ...state, frequency: action.frequency }),
	[UPDATE_STOP_PAYMENT_OPTION]: (state, action) => ({
		...state,
		stopPaymentOption: action.stopPaymentOption,
		stopPaymentOnNumber: undefined,
		stopPaymentOnDate: action.stopPaymentOnDate,
	}),
	[UPDATE_STOP_PAYMENT_ON_NUMBER]: (state, action) => ({ ...state, stopPaymentOnNumber: action.stopPaymentOnNumber }),
	[UPDATE_STOP_PAYMENT_ON_DATE]: (state, action) => ({ ...state, stopPaymentOnDate: action.stopPaymentOnDate }),
};

const payment = (state = defaultPaymentState(), action) => (paymentHandlers[action.type] || ((s) => s))(state, action);

export const identifyPaymentType = (payment) => {
	if (payment.frequency && payment.frequency !== 'SINGLE ONE OFF') {
		return 'repeating';
	}
	return 'single';
};

const intitPayments = (payments) =>
	payments.map((payment) => {
		const frequency = paymentFrequencyOptions.some((option) => option.value === payment.frequency)
			? payment.frequency
			: undefined;
		const amount = fixAmount(stripCurrencysIllegalCharacters(payment.amount));

		return {
			...payment,
			type: identifyPaymentType(payment),
			amount,
			frequency,
			initialReference: payment.reference,
		};
	});

const payments = (state = initialPayments, action) => {
	if (Object.keys(paymentHandlers).includes(action.type)) {
		return state.map((item) => (item.id === action.id ? payment(item, action) : item));
	}
	if ([INIT_PAYMENTS, INIT_PAYMENTS_FROM_TEMPLATE, INIT_PAYMENTS_FOR_TEMPLATE].includes(action.type)) {
		return intitPayments(action.payments);
	}

	if (action.type === UPDATE_PAYMENT_ID) {
		return state.map((payment) => {
			if (payment.id !== action.id) return payment;
			const newId = uuid.v1();
			const authorisation = payment.authorisation
				? { authorisation: { ...payment.authorisation, source_id: newId } }
				: {};
			return { ...payment, id: newId, ...authorisation };
		});
	}

	if (action.type === COPY_REF_TO_ALL) {
		const { reference, paymentIds } = action;

		return state.map((payment) => {
			if (paymentIds.includes(payment.id)) {
				return { ...payment, reference };
			}
			return payment;
		});
	}

	if (action.type === COPY_DATE_TO_ALL) {
		const { date, paymentIds } = action;

		return state.map((payment) => {
			if (paymentIds.includes(payment.id)) {
				return { ...payment, date };
			}
			return payment;
		});
	}

	if (action.type === UPDATE_ALL_DATES) {
		return state.map((payment) => ({ ...payment, date: action.date }));
	}

	if (action.type === UPDATE_FROM_ACCOUNT) {
		return state.map((payment) => {
			return {
				...payment,
				fromAccount: action.accountId,
				toAccount: payment.toAccount === action.accountId ? '' : payment.toAccount,
			};
		});
	}

	if (action.type === UPDATE_ALL_FROM_ACCOUNTS) {
		return state.map((payment) => {
			if (payment.toAccount === action.accountId) {
				return { ...payment, fromAccount: action.accountId, toAccount: '' };
			}
			return { ...payment, fromAccount: action.accountId };
		});
	}

	if (action.type === ADD_PAYMENT) {
		const initialPaymentState = getInitialPaymentState(state, defaultPaymentState(action.payment));
		return [...state, payment(initialPaymentState, action)];
	}

	if (action.type === ADD_MULTIPLE_PAYMENTS) {
		return [...state, ...action.payments.map(defaultPaymentState)];
	}

	if (action.type === REMOVE_PAYMENT) {
		return state.filter((payment) => payment.id !== action.id);
	}

	if (action.type === SUBMIT_PAYMENT_REVIEW_SUCCESS) {
		const userId = get(action, 'meta.userId');

		return state.map((payment) => {
			const response = action.payload.apiResponses.find((item) => item.id === payment.id);

			const {
				authorisation,
				singleAuthoriserList,
				multiAuthoriserList,
				authorisers,
				authorisersGroup,
				isDefaultAuthorisers,
			} = getAuthorisationInfo(userId, response);

			const isTRAObligate = isPaymentTRAObligate(
				authorisation.ben_id_present,
				singleAuthoriserList,
				authorisation.status,
				userId
			);

			return {
				...payment,
				authorisation,
				singleAuthoriserList,
				multiAuthoriserList,
				authorisers,
				authorisersGroup,
				isDefaultAuthorisers,
				isTRAObligate,
			};
		});
	}

	if (action.type === SUBMIT_PAYMENT_DAILY_LIMIT_REVIEW_SUCCESS) {
		return state.map((payment) => {
			const response = action.payload.apiResponses.find((item) => item.id === payment.id);
			if (isConflictAndDailyLimitError(response)) {
				const authorisation = { ...get(response, 'body', {}), responseCode: get(response, 'code', 0) };
				return { ...payment, authorisation };
			}
			return payment;
		});
	}

	if (action.type === SUBMIT_CONFIRMED_PAYMENT_SUCCESS) {
		return state.map((payment) => {
			const response = action.payload.apiResponses.find((item) => item.id === payment.id);
			const confirmation = {
				...response.body,
				responseCode: response.code,
				authType: get(response, 'body.verification_type', ''),
			};

			return { ...payment, confirmation };
		});
	}

	if (action.type === SUBMIT_CONFIRMED_BATCH_PAYMENTS_SUCCESS) {
		return state.map((payment) => {
			const response = action.payload.apiResponses.find((item) => item.id === payment.id);
			const confirmation = {
				...response.body,
				responseCode: response.code,
				authType: get(response, 'body.verification_type', ''),
			};

			return { ...payment, confirmation };
		});
	}

	if (action.type === SUBMIT_REPEATING_PAYMENT_REVIEW_SUCCESS) {
		const userId = get(action, 'meta.userId');

		return state.map((payment) => {
			const response = action.payload;

			const {
				authorisation,
				singleAuthoriserList,
				multiAuthoriserList,
				authorisers,
				authorisersGroup,
				isDefaultAuthorisers,
			} = getAuthorisationInfo(userId, response);

			return {
				...payment,
				authorisation,
				singleAuthoriserList,
				multiAuthoriserList,
				authorisers,
				authorisersGroup,
				isDefaultAuthorisers,
			};
		});
	}

	if (action.type === SUBMIT_REPEATING_PAYMENT_REVIEW_FAILURE) {
		if (action.payload.status === 409) {
			const response = get(action, 'payload.response', {});
			const authorisation = { responseCode: 409, ...response };
			return state.map((payment) => ({ ...payment, authorisation }));
		}

		return state;
	}

	if (action.type === SUBMIT_CONFIRMED_REPEATING_PAYMENT_SUCCESS) {
		const id = get(action, 'payload.id', '');
		if (id === '') return state;
		const confirmation = { ...action.payload, responseCode: 202 };
		return state.map((payment) => ({ ...payment, confirmation }));
	}

	if (action.type === SUBMIT_CONFIRMED_REPEATING_PAYMENT_FAILURE) {
		const status = get(action, 'payload.status', '');
		if (status === '') return state;

		const response = get(action, 'payload.response');
		const authType = get(response, 'verification_type', '');
		const confirmation = {
			responseCode: status,
			authType,
			...response,
		};
		return state.map((payment) => ({ ...payment, confirmation }));
	}

	if (action.type === BENEFICIARY_CREATE_SUCCESS) {
		if (action.payload.apiResponses && action.payload.apiResponses.filter((r) => r.code !== 201)) {
			return state.map((payment) => {
				const targetBenID = get(
					head(
						action.payload.apiResponses
							.filter((r) => r.code === 201)
							.filter((resp) => get(resp, 'body.account_id') === payment.toAccount)
					),
					'body.id'
				);

				if (targetBenID) {
					return { ...payment, beneficiary_id: targetBenID };
				}

				return payment;
			});
		}
		return state;
	}

	if ([RESET_APP, CLEAR_PAYMENTS].includes(action.type)) {
		return [];
	}

	if (action.type === RESET_PAYMENT_AUTHORISATION) {
		return state.map((payment) => {
			if (payment.id !== action.id) return payment;
			const { authorisation, ...newPayment } = payment;
			return newPayment;
		});
	}

	if (action.type === RESET_DAILY_LIMIT_AUTHORISATION) {
		return state.map((payment) => {
			const code = get(payment, 'authorisation.error.code', 0);
			if (code === DAILY_LIMIT_ERROR_CODE) {
				const { authorisation, ...newPayment } = payment;
				return newPayment;
			}
			return payment;
		});
	}

	if (action.type === CUT_OFF_MISSED_UPDATE_PAYMENT_DATE) {
		const newDate = moment(action.nextAvailableDate);
		return state.map((payment) => {
			if (payment.date.isBefore(newDate)) {
				return { ...payment, date: newDate };
			}
			return payment;
		});
	}

	if (action.type === SET_AUTHORISERS) {
		return state.map((payment) => {
			if (payment.id !== action.paymentId) return payment;
			return { ...payment, authorisers: action.authorisers };
		});
	}

	return state;
};

export default payments;
